import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { IoCheckmarkDoneSharp } from "react-icons/io5";
import ConfirmationModal from "../../../components/Modal/ConfirmationModal";
import TableComp from "../../../components/Table";
import { ColumnFilter } from "../../../components/Table/Filters";
import config from "../../../constants/config";
import { DownloadDocumentButton } from "../../../context/DownloadDocumentButton";
import { generateDriversEditViewDeleteColumn } from "../../../utils";
import { fetchService } from "../../../utils/api";
import { errorToast } from "../../../utils/toastFunc";
import { ButtonWithTooltip } from "../../FinanceApplication/components/Common";
import {
  NoteCell,
  wrapCell,
} from "../../SurchargeMaintenance/components/CommonCell";
import { getExpirationColor } from "../Index";
import { driversTableClickEvents } from "../utils";

function ACNSignOff() {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(false);
  const { data: acnDrivers } = useQuery({
    queryKey: ["acn-drivers"],
    queryFn: async () => {
      const response = await fetchService({
        url: config.weeklyDocument["weekly-docs"],
      });
      return response;
    },
    refetchOnWindowFocus: false,
  });

  const { mutate: updateACnDocumentStatus, queryClient } = useMutation({
    mutationFn: async ({ id, status }) => {
      const response = await fetchService({
        url: `${config.weeklyDocument["weekly-docs-approve"]}/${id}?acnDocFileApproved=${status}&acnInvoiceFileApproved=false`,
      });
      return response;
    },
    onSuccess: () => {},
    onError: (error) => {
      errorToast({ mes: "Error updating ACN document status" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["acn-drivers"],
      });
    },
  });

  const handleConfirmButtonClick = (action, message) => {
    setConfirmAction(() => action);
    setConfirmMessage(message);
    setShowConfirmModal(true);
  };

  const handleConfirm = () => {
    if (confirmAction) {
      confirmAction();
    }
    setShowConfirmModal(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "employeeId",
        Filter: ColumnFilter,
      },
      {
        Header: "Name",
        accessor: "fullName",
        Filter: ColumnFilter,
        width: 150,
        Cell: ({ value }) => NoteCell(value),
      },
      {
        Header: "Driver Type",
        accessor: "driverType",
        Filter: ColumnFilter,
      },
      {
        Header: "License No.",
        accessor: "licenceNumber",
        Filter: ColumnFilter,
      },
      {
        Header: "License Exp. Status",
        accessor: "licenceExpiryDate",
        Filter: ColumnFilter,
        Cell: ({ value }) => {
          const { color, tooltip, background } = getExpirationColor(value);
          return (
            <div
              className="d-flex justify-content-center align-items-center py-2"
              style={{ color, background, width: " 100%" }}
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              title={value}
            >
              {tooltip}
            </div>
          );
        },
      },
      {
        Header: "Contact No.",
        accessor: "contactNumber",
        Filter: ColumnFilter,
      },
      {
        Header: "Upload Date",
        accessor: "uploadDate",
        Filter: ColumnFilter,
      },
      {
        Header: wrapCell("ACN Doc Sign Off"),
        accessor: "acnDocUploaded",
        disableFilters: true,
        width: 150,
        Cell: ({ row: { original } }) => (
          <DownloadDocumentButton
            docName={original?.acnDocUploaded}
            userId={original?.userId}
            url={
              config.weeklyDocument["weekly-docs"] +
              "/" +
              original?.driverId +
              "/" +
              original?.docId +
              "/" +
              "ACN_DOCUMENT"
            }
            fileName={`ACN_DOCUMENT-${original?.employeeId}.pdf`}
          />
        ),
      },
      {
        Header: "Status",
        accessor: "acnDocFileApproved",
        disableFilters: true,
        Cell: ({ value }) => {
          return value ? "Approved" : "Rejected";
        },
      },
      {
        Header: "Approve",
        disableFilters: true,
        width: 100,
        Cell: ({ row }) => (
          <ButtonWithTooltip
            variant="success"
            onClick={() =>
              handleConfirmButtonClick(() => {
                /* Call Confirm API here */
                updateACnDocumentStatus({
                  id: row?.original?.docId,
                  status: true,
                });
              }, "Are you sure you want to approve?")
            }
            icon={<IoCheckmarkDoneSharp />}
            tooltipText="Approve"
          />
        ),
      },
      generateDriversEditViewDeleteColumn(
        "driverId",
        false,
        driversTableClickEvents
      ),
    ],
    []
  );
  return (
    <div>
      <TableComp columns={columns} data={acnDrivers} />
      <ConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={handleConfirm}
        message={confirmMessage}
      />
    </div>
  );
}

export default ACNSignOff;
